import { ChevronDown } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";

/**
 * Tarjeta de Ajustes contraíble: pulsar la cabecera para desplegar el contenido.
 */
export default function AjustesSeccion({
  id,
  title,
  subtitle,
  icon: Icon,
  open,
  onOpenChange,
  headerRight,
  children,
  className = "",
  borderClass = "border-zinc-100",
  headerBg = "",
  iconClass = "bg-zinc-100 text-zinc-600",
  defaultOpen = false,
}) {
  const controlled = open !== undefined && typeof onOpenChange === "function";
  return (
    <Collapsible
      id={id}
      open={controlled ? open : undefined}
      defaultOpen={!controlled ? defaultOpen : undefined}
      onOpenChange={controlled ? onOpenChange : undefined}
      className={cn(
        "group scroll-mt-4 rounded-xl border bg-white shadow-sm",
        borderClass,
        className
      )}
    >
      <div className={cn("flex items-center gap-2 border-b px-4 py-3 sm:px-6", headerBg || "border-zinc-50")}>
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="flex min-w-0 flex-1 items-center gap-2.5 text-left outline-none"
          >
            {Icon && (
              <div
                className={cn(
                  "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-white shadow-sm",
                  iconClass
                )}
              >
                <Icon className="h-4 w-4" strokeWidth={1.5} />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <p className="font-display text-sm font-medium text-zinc-900 sm:text-base">{title}</p>
              {subtitle && <p className="truncate text-xs text-zinc-400">{subtitle}</p>}
            </div>
            <ChevronDown className="h-5 w-5 shrink-0 text-zinc-400 transition-transform duration-200 group-data-[state=open]:rotate-180" />
          </button>
        </CollapsibleTrigger>
        {headerRight && (
          <div className="flex shrink-0 items-center gap-2" onClick={(e) => e.stopPropagation()}>
            {headerRight}
          </div>
        )}
      </div>
      <CollapsibleContent className="data-[state=closed]:animate-collapse-up data-[state=open]:animate-collapse-down">
        <div className="space-y-4 px-4 py-5 sm:px-6">{children}</div>
      </CollapsibleContent>
    </Collapsible>
  );
}

/** Subbloque dentro de una tarjeta grande (jornada, etc.). */
export function AjustesSubbloque({
  id,
  title,
  subtitle,
  icon: Icon,
  iconColor = "text-zinc-600",
  borderClass = "border-zinc-100 bg-zinc-50/40",
  headerRight,
  children,
  defaultOpen = false,
}) {
  return (
    <Collapsible id={id} defaultOpen={defaultOpen} className={cn("group scroll-mt-4 rounded-xl border p-0", borderClass)}>
      <div className="flex items-center gap-2 px-4 py-3">
        <CollapsibleTrigger asChild>
          <button type="button" className="flex min-w-0 flex-1 items-center gap-2 text-left outline-none">
            {Icon && <Icon className={cn("h-4 w-4 shrink-0", iconColor)} />}
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-zinc-900">{title}</p>
              {subtitle && <p className="text-xs text-zinc-500 line-clamp-2">{subtitle}</p>}
            </div>
            <ChevronDown className="h-4 w-4 shrink-0 text-zinc-400 transition-transform group-data-[state=open]:rotate-180" />
          </button>
        </CollapsibleTrigger>
        {headerRight && (
          <div className="flex shrink-0 items-center gap-2" onClick={(e) => e.stopPropagation()}>
            {headerRight}
          </div>
        )}
      </div>
      <CollapsibleContent>
        <div className="space-y-4 border-t border-black/5 px-4 py-4">{children}</div>
      </CollapsibleContent>
    </Collapsible>
  );
}
